// src/components/ProjectFormModal.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createProject, fetchDonors, fetchProgramManagers } from '../api/projectCreationApi';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { parseISO, addDays, isWeekend, isAfter, format } from 'date-fns';
import { FiX, FiBriefcase, FiMapPin, FiFileText, FiCalendar, FiClock, FiFlag, FiUser, FiDollarSign, FiFolder, FiSave, FiAlertCircle } from 'react-icons/fi';

const calculateEndDate = (start, days) => {
  if (!start || !days) return '';
  let date = parseISO(start);
  let workdays = 0;
  while (workdays < days) {
    date = addDays(date, 1);
    if (!isWeekend(date)) {
      workdays++;
    }
  }
  return date;
};

const calculateDuration = (start, end) => {
  if (!start || !end || isAfter(parseISO(start), parseISO(end))) return '';
  let date = parseISO(start);
  let workdays = 0;
  while (isAfter(parseISO(end), date)) {
    if (!isWeekend(date)) {
      workdays++;
    }
    date = addDays(date, 1);
  }
  return workdays;
};

// Helper to generate the Preview Code on the frontend
const getBudgetCodePreview = (title) => {
  if (!title) return 'HTC---';
  const cleanTitle = title.replace(/[^a-zA-Z0-9\s]/g, '').trim();
  const words = cleanTitle.split(/\s+/);
  let initials = words.length > 0 
    ? words.map(w => w[0]).join('').toUpperCase().substring(0, 3) 
    : '';
  if (!initials) initials = 'GEN';
  // We default to 001 on the frontend for preview, backend determines exact number
  return `HTC-${initials}-001`;
};

const ProjectFormModal = ({ isOpen, onClose, onSuccess }) => {
  const modalRef = useRef(null);
  const [formData, setFormData] = useState({
    projectTitle: '',
    location: '',
    description: '',
    projectCategories: '',
    startDate: '',
    endDate: '',
    duration: '',
    status: 'Planning',
    donor: [],
    assignedProgramManager: '',
    budgetCode: '',
     projectBudget: '',
  });

  const [donors, setDonors] = useState([]);
  const [programManagers, setProgramManagers] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      try {
        const [donorsData, managersData] = await Promise.all([
          fetchDonors(),
          fetchProgramManagers(),
        ]);
        setDonors(donorsData);
        setProgramManagers(managersData);
      } catch (error) {
        setGeneralError("Failed to load form data. Please check your connection and try again.");
        console.error(error);
        setProgramManagers([]);
      }
    };

    loadData();
  }, [isOpen]);

  const donorOptions = useMemo(() => {
    if (!donors) return [];
    return donors.map(donor => ({
      value: donor._id,
      label: donor.displayName || donor.name || donor.organization,
    }));
  }, [donors]);

  const programManagerOptions = useMemo(() => {
    if (!Array.isArray(programManagers)) {
      console.warn("programManagers is not an array, returning empty options.", programManagers);
      return [];
    }
    return programManagers.map(manager => ({
      value: manager._id,
      label: `${manager.firstName} ${manager.lastName}`,
    }));
  }, [programManagers]);

  const statusOptions = [
    { value: 'Planning', label: 'Planning' },
    { value: 'In Progress', label: 'In Progress' },
    { value: 'On Hold', label: 'On Hold' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Cancelled', label: 'Cancelled' },
    { value: 'Archived', label: 'Archived' },
  ];

  const orangeTheme = theme => ({
    ...theme,
    colors: {
      ...theme.colors,
      primary25: '#FFF5E6',
      primary: '#F68B1F',
    },
  });

  const validateForm = () => {
    const newErrors = {};
    
    // Validate project title
    if (!formData.projectTitle.trim()) {
      newErrors.projectTitle = "Project title is required. Please enter a name for the project.";
    } else if (formData.projectTitle.trim().length < 3) {
      newErrors.projectTitle = "Project title must be at least 3 characters long.";
    }
    
    // Validate location
    if (!formData.location.trim()) {
      newErrors.location = "Project location is required. Please specify where this project will take place.";
    }
    
    // Validate description
    if (!formData.description.trim()) {
      newErrors.description = "Project description is required. Please provide details about this project.";
    } else if (formData.description.trim().length < 10) {
      newErrors.description = "Description must be at least 10 characters long. Please provide more details.";
    }
    
    // Validate start date
    if (!formData.startDate) {
      newErrors.startDate = "Start date is required. Please select when this project will begin.";
    }


    
    // Validate end date
    if (!formData.endDate) {
      newErrors.endDate = "End date is required. Please select when this project will conclude.";
    } else if (formData.startDate && isAfter(parseISO(formData.startDate), parseISO(formData.endDate))) {
      newErrors.endDate = "End date must be after the start date. Please correct the date range.";
    }
    
    // Validate donors
    if (!formData.donor || formData.donor.length === 0) {
      newErrors.donor = "At least one donor must be selected. Please specify who is funding this project.";
    }

     // Validate Project Budget
    if (!formData.projectBudget || formData.projectBudget <= 0) {
      newErrors.projectBudget = "Project budget is required and must be greater than 0.";
    }
    
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData(prev => {
      const newForm = { ...prev, [name]: value };

      if (name === 'projectTitle') {
        newForm.budgetCode = getBudgetCodePreview(value);
      }

      if (name === 'startDate' && newForm.duration) {
        const calculatedEndDate = calculateEndDate(value, Number(newForm.duration));
        newForm.endDate = format(calculatedEndDate, 'yyyy-MM-dd');
      } else if (name === 'duration' && newForm.startDate) {
        const calculatedEndDate = calculateEndDate(newForm.startDate, Number(value));
        newForm.endDate = format(calculatedEndDate, 'yyyy-MM-dd');
      }
      else if ((name === 'startDate' && newForm.endDate) || (name === 'endDate' && newForm.startDate)) {
        const calculatedDuration = calculateDuration(newForm.startDate, newForm.endDate);
        newForm.duration = calculatedDuration;
      }
      return newForm;
    });
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSelectChange = (selectedOptions, { name }) => {
    if (name === 'donor') {
      setFormData(prev => ({
        ...prev,
        [name]: selectedOptions ? selectedOptions.map(opt => opt.value) : [],
      }));
    } else if (name === 'status') {
      setFormData(prev => ({
        ...prev,
        [name]: selectedOptions ? selectedOptions.value : '',
      }));
    } else if (name === 'assignedProgramManager') {
      setFormData(prev => ({
        ...prev,
        [name]: selectedOptions ? selectedOptions.value : '',
      }));
    }
    
    // Clear error for this field when user makes a selection
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      setGeneralError("Please correct the errors in the form before submitting.");
      return;
    }
    
    setIsSaving(true);
    setGeneralError('');
    
    const payload = {
      ...formData,
      projectCategories: (formData.projectCategories || "")
        .split(',')
        .map((cat) => cat.trim())
        .filter(Boolean),
      duration: undefined,
    };
    
    try {
      await createProject(payload);
      toast.success('Project created successfully!');
      onSuccess();
      onClose();
    } catch (err) {
      console.error("Create project error:", err);
      
      if (err.response) {
        // Handle server validation errors
        if (err.response.status === 400 && err.response.data && err.response.data.errors) {
          const serverErrors = {};
          err.response.data.errors.forEach(error => {
            serverErrors[error.param] = error.msg;
          });
          setErrors(serverErrors);
          setGeneralError("Please correct the errors in the form.");
        } else if (err.response.data && err.response.data.message) {
          setGeneralError(`Failed to create project: ${err.response.data.message}`);
        } else {
          setGeneralError("Failed to create project. The server returned an error.");
        }
      } else if (err.request) {
        // Network error
        setGeneralError("Network error. Please check your connection and try again.");
      } else {
        // Other error
        setGeneralError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 backdrop-blur-sm p-4">
      <div 
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md relative transform transition-all duration-300 ease-out flex flex-col max-h-[90vh]"
      >
        {/* Fixed Header with Orange Theme */}
        <div className="bg-gradient-to-r from-orange-300 to-orange-400 rounded-t-2xl p-4 sm:p-6 border-b border-orange-500 flex-shrink-0">
          <div className="flex justify-between items-center">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
              <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                <FiBriefcase className="w-5 h-5 sm:w-6 sm:h-6 text-black" />
              </div>
              <div>
                <h2 className="text-lg sm:text-xl font-bold text-black">
                  New Project
                </h2>
                <div className="flex items-center mt-1 text-black text-sm">
                  <FiFolder className="w-3 h-3 mr-1" />
                  <span>Programs Department</span>
                </div>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="p-2 rounded-lg transition-all duration-200 text-orange-200 hover:bg-white/20 hover:text-white"
            >
              <FiX size={20} />
            </button>
          </div>
        </div>

        {/* General Error Message */}
        {generalError && (
          <div className="mx-6 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <FiAlertCircle className="text-red-500 mt-0.5 mr-2 flex-shrink-0" />
            <p className="text-red-700 text-sm">{generalError}</p>
          </div>
        )}

        {/* Scrollable Form Body */}
        <div className="flex-grow overflow-y-auto p-4 sm:p-6">
          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5">

            {/* Project Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Title <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiBriefcase className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="projectTitle"
                  placeholder="Enter project title"
                  value={formData.projectTitle}
                  onChange={handleChange}
                  required
                  className={`w-full pl-10 pr-3 py-3 text-sm rounded-lg border ${errors.projectTitle ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200`}
                />
              </div>
              {errors.projectTitle && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <FiAlertCircle className="w-4 h-4 mr-1" />
                  {errors.projectTitle}
                </p>
              )}
            </div>

            {/* Location */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiMapPin className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="location"
                  placeholder="Enter project location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  className={`w-full pl-10 pr-3 py-3 text-sm rounded-lg border ${errors.location ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200`}
                />
              </div>
              {errors.location && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <FiAlertCircle className="w-4 h-4 mr-1" />
                  {errors.location}
                </p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                  <FiFileText className="h-5 w-5 text-gray-400" />
                </div>
                <textarea
                  name="description"
                  placeholder="Describe this project..."
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  required
                  className={`w-full pl-10 pr-3 py-3 text-sm rounded-lg border ${errors.description ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200 resize-none`}
                />
              </div>
              {errors.description && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <FiAlertCircle className="w-4 h-4 mr-1" />
                  {errors.description}
                </p>
              )}
            </div>




            {/* ✨ NEW: Project Budget */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Total Project Budget (USD) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiDollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  name="projectBudget"
                  placeholder="e.g., 50000"
                  value={formData.projectBudget}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                  className={`w-full pl-10 pr-3 py-3 text-sm rounded-lg border ${errors.projectBudget ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200`}
                />
              </div>
              {errors.projectBudget && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <FiAlertCircle className="w-4 h-4 mr-1" />
                  {errors.projectBudget}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">Enter the total expected budget for this project.</p>
            </div>

            {/* Project Categories */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Categories
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiFolder className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="projectCategories"
                  placeholder="e.g., Education, Health, Water"
                  value={formData.projectCategories}
                  onChange={handleChange}
                  className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Separate multiple categories with commas</p>
            </div>

            {/* Date Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Start Date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiCalendar className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    required
                    className={`w-full pl-10 pr-3 py-3 text-sm rounded-lg border ${errors.startDate ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200`}
                  />
                </div>
                {errors.startDate && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <FiAlertCircle className="w-4 h-4 mr-1" />
                    {errors.startDate}
                  </p>
                )}
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  End Date <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FiCalendar className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    required
                    min={formData.startDate}
                    className={`w-full pl-10 pr-3 py-3 text-sm rounded-lg border ${errors.endDate ? 'border-red-500' : 'border-gray-300'} focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200`}
                  />
                </div>
                {errors.endDate && (
                  <p className="mt-1 text-sm text-red-600 flex items-center">
                    <FiAlertCircle className="w-4 h-4 mr-1" />
                    {errors.endDate}
                  </p>
                )}
              </div>
            </div>

            {/* Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Duration (working days)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiClock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  name="duration"
                  min="1"
                  value={formData.duration}
                  onChange={handleChange}
                  placeholder="Auto-calculated"
                  className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all duration-200"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">Automatically calculated based on start and end dates</p>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiFlag className="h-5 w-5 text-gray-400" />
                </div>
                <Select
                  name="status"
                  options={statusOptions}
                  value={statusOptions.find(opt => opt.value === formData.status)}
                  onChange={handleSelectChange}
                  className="w-full pl-10 pr-10 py-0"
                  classNamePrefix="select"
                  placeholder="Select a status"
                  isSearchable={false}
                  theme={orangeTheme}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderRadius: '0.5rem',
                      minHeight: '44px',
                      borderColor: state.isFocused ? '#F68B1F' : '#D1D5DB',
                      boxShadow: state.isFocused ? '0 0 0 1px #F68B1F' : 'none',
                      '&:hover': {
                        borderColor: '#F68B1F'
                      }
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? '#F68B1F' : state.isFocused ? '#FFF5E6' : 'white',
                      color: state.isSelected ? 'white' : '#1F2937',
                      '&:hover': {
                        backgroundColor: '#FFF5E6'
                      }
                    }),
                  }}
                />
              </div>
            </div>

            {/* Program Manager */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Program Manager
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiUser className="h-5 w-5 text-gray-400" />
                </div>
                <Select
                  name="assignedProgramManager"
                  options={programManagerOptions}
                  value={programManagerOptions.find(opt => opt.value === formData.assignedProgramManager)}
                  onChange={handleSelectChange}
                  className="w-full pl-10 pr-10 py-0"
                  classNamePrefix="select"
                  placeholder="Select a program manager (optional)"
                  isClearable
                  theme={orangeTheme}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderRadius: '0.5rem',
                      minHeight: '44px',
                      borderColor: state.isFocused ? '#F68B1F' : '#D1D5DB',
                      boxShadow: state.isFocused ? '0 0 0 1px #F68B1F' : 'none',
                      '&:hover': {
                        borderColor: '#F68B1F'
                      }
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? '#F68B1F' : state.isFocused ? '#FFF5E6' : 'white',
                      color: state.isSelected ? 'white' : '#1F2937',
                      '&:hover': {
                        backgroundColor: '#FFF5E6'
                      }
                    }),
                  }}
                />
              </div>
            </div>

            
            {/* Budget Code Field (Visible, Read-Only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Budget Code (Auto-Generated)
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiFlag className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  name="budgetCode"
                  value={formData.budgetCode}
                  readOnly
                  className="w-full pl-10 pr-3 py-3 text-sm rounded-lg border border-gray-300 bg-gray-100 text-gray-600 cursor-not-allowed focus:outline-none"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">System-generated code based on project title.</p>
            </div>


            {/* Donor(s) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Donor(s) <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="absolute top-3 left-3 flex items-start pointer-events-none">
                  <FiDollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <Select
                  name="donor"
                  options={donorOptions}
                  value={donorOptions.filter(opt => formData.donor.includes(opt.value))}
                  onChange={handleSelectChange}
                  isMulti
                  className={`w-full pl-10 pr-10 py-0 ${errors.donor ? 'select-error' : ''}`}
                  classNamePrefix="select"
                  placeholder="Select donor(s)"
                  theme={orangeTheme}
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderRadius: '0.5rem',
                      minHeight: '44px',
                      borderColor: errors.donor ? '#EF4444' : (state.isFocused ? '#F68B1F' : '#D1D5DB'),
                      boxShadow: state.isFocused ? '0 0 0 1px #F68B1F' : 'none',
                      '&:hover': {
                        borderColor: '#F68B1F'
                      }
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected ? '#F68B1F' : state.isFocused ? '#FFF5E6' : 'white',
                      color: state.isSelected ? 'white' : '#1F2937',
                      '&:hover': {
                        backgroundColor: '#FFF5E6'
                      }
                    }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#FFF5E6',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: '#F68B1F',
                      fontWeight: '500',
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: '#F68B1F',
                      '&:hover': {
                        backgroundColor: '#F68B1F',
                        color: 'white',
                      },
                    }),
                  }}
                />
              </div>
              {errors.donor && (
                <p className="mt-1 text-sm text-red-600 flex items-center">
                  <FiAlertCircle className="w-4 h-4 mr-1" />
                  {errors.donor}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row sm:justify-end space-y-3 sm:space-y-0 sm:space-x-3 pt-4">
              <button
                type="button"
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-all duration-200 order-2 sm:order-1"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-orange-400 to-orange-500 rounded-lg hover:from-orange-500 hover:to-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 transition-all duration-200 flex items-center justify-center space-x-2 order-1 sm:order-2"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <FiSave className="w-4 h-4" />
                    <span>Save Project</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProjectFormModal;